import React from 'react';
import { Box, Typography, Skeleton, Alert } from '@mui/material';
import { useApiGet } from '../../utils/httpClientTs';
import { getDashboard } from '../../endpoints/annualReportsTs';
import { AnnualReportsDashboardResponse } from '../../types/annualReports';
import NotificationsSection from './components/NotificationsSection';
import LocalChapterView from './components/LocalChapterView';
import OverviewView from './components/OverviewView';

const AnnualReportsDashboard: React.FC = () => {
  const { data, isLoading, error } = useApiGet<AnnualReportsDashboardResponse>(
    ['annual-reports-dashboard'],
    getDashboard,
    {
      enabled: true,
      retry: 1,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    }
  );

  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="text" width="60%" height={40} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={400} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error">
          Failed to load dashboard. Please try refreshing the page.
        </Alert>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box>
        <Alert severity="info">No dashboard data available.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Annual Reports Dashboard
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Year: {data.year}
      </Typography>

      <NotificationsSection notifications={data.notifications} />

      {data.persona === 'local_chapter' && data.local_chapter && (
        <LocalChapterView data={data.local_chapter} />
      )}

      {(data.persona === 'spd_chapter' || data.persona === 'international') &&
        data.overview && <OverviewView data={data.overview} />}
    </Box>
  );
};

export default AnnualReportsDashboard;
